<template>
  <div class="flex flex-col gap-[8px] w-full">
    <div class="flex flex-row flex-wrap gap-x-[12px] gap-y-[4px]">
      <div
        v-for="rule in rules"
        :key="rule.key"
        :class="ruleState(rule.key) === 'missing' ? 'text-error' : 'text-grays-900'"
        class="flex flex-row items-center gap-[4px]"
      >
        <span
          :class="ruleState(rule.key) === 'met' ? 'text-success' : ''"
          class="material-symbols-rounded text-base"
        >
          {{ ruleState(rule.key) === "missing" ? "cancel" : "check_circle" }}
        </span>
        <p class="text-xs font-medium">{{ rule.label }}</p>
      </div>
    </div>

    <div v-if="password && showMeter" class="flex flex-col gap-[4px]" aria-live="polite">
      <div class="h-[3px] w-full rounded-full bg-grays-300" aria-hidden="true">
        <div
          :class="level.color"
          :style="{ width: level.width }"
          class="h-full rounded-full transition-all duration-300"
        ></div>
      </div>
      <p class="text-xs text-grays-700">{{ level.label }}</p>
      <p v-if="hint" class="text-xs text-grays-900">{{ hint }}</p>
    </div>
  </div>
</template>

<script>
import { evaluatePassword, loadZxcvbn } from "@/utils/passwordRules";

// "Not guessable" (zxcvbn + personal details) has no line of its own: the
// meter shows it, and only a valid password reaches "strong".
const RULES = [
  { key: "length", label: "10 תווים" },
  { key: "upper", label: "אות גדולה" },
  { key: "lower", label: "אות קטנה" },
  { key: "digit", label: "ספרה" },
  { key: "noSpace", label: "ללא רווחים" },
];

const LEVELS = {
  weak: { label: "סיסמה חלשה", color: "bg-error", width: "33%" },
  medium: { label: "סיסמה בינונית", color: "bg-orange-400", width: "50%" },
  strong: { label: "סיסמה חזקה", color: "bg-success", width: "100%" },
};

export default {
  name: "password-checks",
  props: {
    password: {
      type: String,
      default: "",
    },
    // What the password must not equal: { phones, username, tzl, tzw, email }
    context: {
      type: Object,
      default: () => ({}),
    },
    error: {
      type: Boolean,
      default: false,
    },
    shouldShowError: {
      type: Boolean,
      default: false,
    },
    // The popup hides the meter while it shows a server refusal in its place.
    showMeter: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:valid"],
  data() {
    return { zxcvbn: null, zxcvbnFailed: false, rules: RULES };
  },
  computed: {
    result() {
      return evaluatePassword(this.password, this.context, this.zxcvbn, {
        scoreUnavailable: this.zxcvbnFailed,
      });
    },
    level() {
      if (this.result.valid) return LEVELS.strong;
      if (!this.result.personal && this.result.score >= 2) return LEVELS.medium;
      return LEVELS.weak;
    },
    // All four lines are green but the password is still refused: say why.
    hint() {
      const { checks, personal } = this.result;
      const basicsMet = RULES.every((rule) => checks[rule.key]);
      if (!basicsMet || checks.strong) return "";
      if (!this.zxcvbn && !this.zxcvbnFailed) return "בודקים את חוזק הסיסמה…";
      return personal
        ? "הסיסמה לא יכולה להיות מספר הטלפון, שם המשתמש, תעודת הזהות או המייל שלך."
        : "הסיסמה עדיין קלה לניחוש. אפשר להוסיף עוד מילה או כמה תווים.";
    },
  },
  watch: {
    "result.valid": {
      immediate: true,
      handler(valid) {
        this.$emit("update:valid", valid);
      },
    },
  },
  mounted() {
    this.loadScorer(3);
  },
  methods: {
    // If zxcvbn never loads, the customer must not be stuck on "weak" with the
    // button off: give up after a few tries and let the server judge.
    loadScorer(triesLeft) {
      loadZxcvbn()
        .then((fn) => {
          this.zxcvbn = fn;
          this.zxcvbnFailed = false;
        })
        .catch(() => {
          if (triesLeft > 1) {
            setTimeout(() => this.loadScorer(triesLeft - 1), 1500);
          } else {
            this.zxcvbnFailed = true;
          }
        });
    },
    // met → green; missing (after a submit, or forms that always show errors) → red; otherwise neutral
    ruleState(key) {
      if (this.result.checks[key]) return "met";
      return this.shouldShowError || this.error ? "missing" : "pending";
    },
  },
};
</script>
